import type { FC, ReactNode } from "react";
import { Box, Button, Column, Dialog, Row, Text } from "@hightouch/htui";
import {
  CONTEXT_STARTER_TEMPLATES,
  type ContextStarterTemplate,
} from "./starter-templates";

type StarterTemplateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: ContextStarterTemplate) => void;
};

/** The Files "Start from a template" picker: one card per starter template. */
export const StarterTemplateDialog: FC<StarterTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => (
  <Dialog
    isOpen={isOpen}
    onClose={onClose}
    variant="form"
    width="3xl"
    title="Choose a starting point"
    actions={
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
    }
  >
    <Column gap={4}>
      <Text>
        Pick the kind of context you want to add. Templates give you a
        structure, so you don&apos;t start from a blank page.
      </Text>
      <Box
        display="grid"
        gridTemplateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        gap={4}
      >
        {CONTEXT_STARTER_TEMPLATES.map((template) => (
          <StarterTemplateCard
            key={template.id}
            template={template}
            onClick={() => onSelectTemplate(template)}
          />
        ))}
      </Box>
    </Column>
  </Dialog>
);

type StarterTemplateCardProps = {
  template: ContextStarterTemplate;
  onClick: () => void;
};

export const StarterTemplateCard: FC<StarterTemplateCardProps> = ({
  template,
  onClick,
}) => {
  const Icon = template.icon;
  return (
    <CardShell onClick={onClick}>
      <Row
        align="center"
        justify="center"
        flexShrink={0}
        boxSize={9}
        borderRadius="md"
        bg="primary.background"
        color="primary.base"
      >
        <Box as={Icon} boxSize={5} aria-hidden />
      </Row>
      <Column gap={1} minWidth={0}>
        <Text fontWeight="medium">{template.name}</Text>
        <Text size="sm" color="text.secondary">
          {template.description}
        </Text>
      </Column>
    </CardShell>
  );
};

type CardShellProps = {
  onClick: () => void;
  isDashed?: boolean;
  children: ReactNode;
};

export const CardShell: FC<CardShellProps> = ({
  onClick,
  isDashed,
  children,
}) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    display="flex"
    alignItems="flex-start"
    gap={3}
    p={4}
    textAlign="left"
    width="100%"
    height="100%"
    borderWidth="1px"
    borderStyle={isDashed ? "dashed" : "solid"}
    borderColor="base.border"
    borderRadius="lg"
    bg="white"
    cursor="pointer"
    transitionProperty="border-color, background-color"
    transitionDuration="fast"
    transitionTimingFunction="easeOutQuad"
    _hover={{ borderColor: "primary.border", bg: "base.lightBackground" }}
    _focusVisible={{ boxShadow: "outline", outline: "none" }}
  >
    {children}
  </Box>
);
